import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import PrintDocket from "@/components/PrintDocket";
import {
  Printer,
  SignOut,
  ForkKnife,
  CheckCircle,
  Clock,
  ArrowClockwise,
} from "@phosphor-icons/react";

const STATUS_META = {
  new: { label: "NEW", color: "text-[#EF4444]", ring: "border-[#EF4444]" },
  preparing: { label: "PREPARING", color: "text-amber-400", ring: "border-amber-500" },
  done: { label: "DONE", color: "text-[#10B981]", ring: "border-[#10B981]" },
};

function elapsed(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min";
  return `${mins} mins`;
}

export default function KitchenPage() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tick, setTick] = useState(0);
  const [autoPrint, setAutoPrint] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);

  const seenIds = useRef(new Set());
  const initialised = useRef(false);
  const printQueue = useRef([]);
  const printing = useRef(false);

  const doPrint = useCallback((order) => {
    setPrintOrder(order);
    setTimeout(() => {
      window.print();
    }, 120);
  }, []);

  const processQueue = useCallback(() => {
    if (printing.current) return;
    const next = printQueue.current.shift();
    if (!next) return;
    printing.current = true;
    doPrint(next);
  }, [doPrint]);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      // detect new orders for auto-print
      if (initialised.current && autoPrint) {
        const fresh = data.filter((o) => !seenIds.current.has(o.id) && o.status === "new");
        fresh
          .sort((a, b) => a.order_number - b.order_number)
          .forEach((o) => printQueue.current.push(o));
        processQueue();
      }
      data.forEach((o) => seenIds.current.add(o.id));
      initialised.current = true;
      setOrders(data);
    } catch (error) {
      logError("Failed to fetch orders:", error);
    }
  }, [autoPrint, processQueue]);

  useEffect(() => {
    fetchOrders();
    const poll = setInterval(fetchOrders, 5000);
    const timer = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(poll);
      clearInterval(timer);
    };
  }, [fetchOrders]);

  // After the print dialog closes, continue the queue
  useEffect(() => {
    const after = () => {
      printing.current = false;
      setTimeout(processQueue, 300);
    };
    window.addEventListener("afterprint", after);
    return () => window.removeEventListener("afterprint", after);
  }, [processQueue]);

  const manualPrint = (order) => {
    doPrint(order);
  };

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/orders/${order.id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
    } catch (e) {
      toast.error("Could not update order");
    }
  };

  const active = useMemo(() => orders.filter((o) => o.status !== "done"), [orders]);
  const done = useMemo(() => orders.filter((o) => o.status === "done"), [orders]);

  return (
    <div className="min-h-screen bg-[#111827] font-admin text-[#F9FAFB]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#111827] border-b border-[#374151]">
        <div className="px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ForkKnife size={22} weight="bold" className="text-emerald-500" />
            <div>
              <p className="font-mono-kds text-sm tracking-widest">GLEDSWOOD KITCHEN</p>
              <p className="text-[#9CA3AF] text-xs">
                {active.length} active · {done.length} completed
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="auto-print-toggle"
              onClick={() => setAutoPrint((v) => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                autoPrint
                  ? "bg-emerald-600/20 border-emerald-600 text-emerald-400"
                  : "bg-[#1F2937] border-[#374151] text-[#9CA3AF]"
              }`}
            >
              <Printer size={16} weight="bold" />
              Auto-print {autoPrint ? "ON" : "OFF"}
            </button>
            <button
              data-testid="refresh-btn"
              onClick={fetchOrders}
              className="h-9 w-9 rounded-lg bg-[#1F2937] border border-[#374151] flex items-center justify-center hover:bg-[#374151] transition-colors"
            >
              <ArrowClockwise size={16} weight="bold" />
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1F2937] border border-[#374151] text-xs font-medium hover:bg-[#374151] transition-colors"
            >
              <SignOut size={16} weight="bold" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6">
        {active.length === 0 ? (
          <div className="h-[60vh] flex flex-col items-center justify-center text-center">
            <Clock size={48} className="text-[#374151]" />
            <p className="text-[#9CA3AF] mt-4 font-mono-kds text-sm tracking-wider">
              NO ACTIVE ORDERS
            </p>
            <p className="text-[#4B5563] text-sm mt-1">New dine-in orders appear here instantly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {active.map((o) => {
              const meta = STATUS_META[o.status];
              return (
                <div
                  key={o.id}
                  data-testid={`order-ticket-${o.order_number}`}
                  className={`bg-[#1F2937] border-l-4 ${meta.ring} border-t border-r border-b border-[#374151] rounded-lg flex flex-col`}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#374151]">
                    <div>
                      <p className="font-mono-kds text-2xl leading-none">
                        T{o.table_number}
                      </p>
                      <p className="text-[#9CA3AF] text-xs mt-1">#{o.order_number} · {o.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <span className={`font-mono-kds text-[11px] tracking-wider ${meta.color}`}>
                        {meta.label}
                      </span>
                      <p className="text-[#9CA3AF] text-xs mt-1 flex items-center gap-1 justify-end">
                        <Clock size={12} /> {elapsed(o.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="px-4 py-3 flex-1 space-y-2">
                    {o.items.map((it, idx) => (
                      <div key={`${it.menu_item_id}-${idx}`} className="text-sm">
                        <div className="flex gap-2">
                          <span className="font-mono-kds text-emerald-400 font-semibold">
                            {it.quantity}×
                          </span>
                          <span className="text-[#F9FAFB]">{it.name}</span>
                        </div>
                        {it.notes ? (
                          <p className="text-amber-300 text-sm pl-6 italic">↳ {it.notes}</p>
                        ) : null}
                      </div>
                    ))}
                    {o.dietary && (
                      <p className="text-[#EF4444] text-xs font-semibold mt-2 border-t border-[#374151] pt-2">
                        DIETARY: {o.dietary}
                      </p>
                    )}
                    {o.notes && (
                      <p className="text-amber-400 text-xs mt-2 border-t border-[#374151] pt-2">
                        ⚑ {o.notes}
                      </p>
                    )}
                  </div>

                  <div className="p-3 border-t border-[#374151] grid grid-cols-3 gap-2">
                    <button
                      data-testid={`print-order-${o.order_number}`}
                      onClick={() => manualPrint(o)}
                      title="Print docket"
                      className="flex items-center justify-center py-2 rounded-md bg-[#111827] border border-[#374151] hover:bg-[#374151] transition-colors"
                    >
                      <Printer size={16} weight="bold" />
                    </button>
                    {o.status === "new" && (
                      <button
                        data-testid={`start-order-${o.order_number}`}
                        onClick={() => setStatus(o, "preparing")}
                        className="col-span-2 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition-colors"
                      >
                        Start
                      </button>
                    )}
                    {o.status === "preparing" && (
                      <button
                        data-testid={`complete-order-${o.order_number}`}
                        onClick={() => setStatus(o, "done")}
                        className="col-span-2 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                      >
                        <CheckCircle size={15} weight="bold" /> Done
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {done.length > 0 && (
          <section className="mt-10">
            <p className="font-mono-kds text-[#9CA3AF] text-xs tracking-widest mb-3">
              COMPLETED ({done.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {done.slice(0, 24).map((o) => (
                <div
                  key={o.id}
                  className="flex items-center gap-2 bg-[#1F2937] border border-[#374151] rounded-md px-3 py-2 text-xs"
                >
                  <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
                  <span className="font-mono-kds">T{o.table_number}</span>
                  <span className="text-[#9CA3AF]">#{o.order_number}</span>
                  <button
                    data-testid={`reprint-order-${o.order_number}`}
                    onClick={() => manualPrint(o)}
                    className="text-[#9CA3AF] hover:text-[#F9FAFB]"
                  >
                    <Printer size={13} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <PrintDocket order={printOrder} />
    </div>
  );
}
